/**
 * What the organization pages know about allowances: the platform's standard
 * figure (when it can be inferred), the on-demand rate, and the one spend
 * figure the client is given — the viewer's own.
 *
 * @module components/settings/useOrgAllowances
 */
import { useCallback, useMemo } from "react";

import { modelSpendIsShowable, useModelSpend, type ShowableModelSpend } from "../../state/modelSpend";
import type { OrgTeamMember } from "../../state/orgProfile";
import { resolveStandardAllowance } from "./orgMembers.logic";

export interface OrgAllowances {
  readonly standardGbp: number | null;
  readonly overageMultiplier: number | null;
  /** The viewer's own spend this month, when the server reports one. */
  readonly viewerSpend: ShowableModelSpend | null;
  readonly spendFor: (member: Pick<OrgTeamMember, "id">) => ShowableModelSpend | null;
}

export function useOrgAllowances(
  organizationId: string | null,
  team: ReadonlyArray<OrgTeamMember>,
  viewerUserId: string | null,
): OrgAllowances {
  const { status } = useModelSpend(organizationId);
  const viewerSpend = modelSpendIsShowable(status) ? status : null;
  // The figure names its person; trust that over the directory's viewer id.
  const spendUserId = status?.userId ?? viewerUserId;
  const standardGbp = useMemo(
    () => resolveStandardAllowance(team, spendUserId, status),
    [spendUserId, status, team],
  );
  const spendFor = useCallback(
    (member: Pick<OrgTeamMember, "id">) =>
      viewerSpend !== null && member.id === viewerSpend.userId ? viewerSpend : null,
    [viewerSpend],
  );
  return {
    standardGbp,
    overageMultiplier: status?.overageMultiplier ?? null,
    viewerSpend,
    spendFor,
  };
}
